import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Empty } from "./ui";
import { StockDrillDialog } from "./StockDrillDialog";
import { MapPin, AlertTriangle, ChevronRight } from "lucide-react";

const health = (r) => {
  const available = (r.by_status.in_bin || 0) + (r.by_status.at_regional || 0) + (r.by_status.at_site || 0);
  if (available === 0) return { color: "#dc2626", label: "Shortage", bg: "#fef2f2" };
  if (r.over90_pct >= 15) return { color: "#dc2626", label: "Ageing risk", bg: "#fef2f2" };
  if (r.over90_pct >= 5) return { color: "#b45309", label: "Watch", bg: "#fffbeb" };
  return { color: "#00857C", label: "Healthy", bg: "#f0fdfa" };
};

const Bar = ({ label, value, max, color, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex items-center gap-2 text-xs mb-1 w-full text-left group hover:opacity-80 transition-opacity">
    <span className="w-20 text-slate-500 capitalize">{label.replace(/_/g, " ")}</span>
    <div className="flex-1 h-2 bg-slate-100 rounded-sm overflow-hidden">
      <div className="h-full rounded-sm" style={{ width: `${max ? (value / max) * 100 : 0}%`, background: color }} />
    </div>
    <span className="w-6 text-right font-head font-bold text-[#0B2545]">{value}</span>
    <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-[#00857C]" />
  </button>
);

export default function RegionalHeatmap() {
  const [regions, setRegions] = useState([]);
  const [drill, setDrill] = useState(null); // { region, status }
  useEffect(() => { api.get("/reports/regions").then((r) => setRegions(r.data)); }, []);
  const max = Math.max(1, ...regions.map((r) => r.total));

  return (
    <div>
      <PageHeader title="Regional Stock Heatmap" subtitle="Stock levels and ageing by region — click any status bar to drill into locations and serials." />
      {regions.length === 0 ? (
        <Panel><Empty>No regional data yet. Assign a region to your warehouses/sites under Locations, then load stock.</Empty></Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="region-cards">
          {regions.map((r) => {
            const h = health(r);
            return (
              <div key={r.region} data-testid={`region-${r.region}`} className="bg-white border border-slate-200 rounded-sm p-5" style={{ borderTop: `3px solid ${h.color}`, background: h.bg }}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" style={{ color: h.color }} />
                    <span className="font-head font-bold text-[#0B2545]">{r.region}</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide flex items-center gap-1" style={{ background: `${h.color}1a`, color: h.color }}>
                    {h.label === "Shortage" && <AlertTriangle className="h-3 w-3" />}{h.label}
                  </span>
                </div>
                <div className="flex gap-4 mb-3">
                  <div><div className="overline text-slate-500">Total</div><div className="font-head text-2xl font-bold text-[#0B2545]">{r.total}</div></div>
                  <div><div className="overline text-slate-500">Live</div><div className="font-head text-2xl font-bold text-[#0B2545]">{r.live}</div></div>
                  <div><div className="overline text-slate-500">&gt;90d</div><div className="font-head text-2xl font-bold" style={{ color: r.over90 ? "#dc2626" : "#0B2545" }}>{r.over90_pct}%</div></div>
                </div>
                {["in_bin", "at_regional", "at_site", "installed", "active"].filter((s) => r.by_status[s]).map((s) => (
                  <Bar key={s} testid={`region-bar-${r.region}-${s}`} label={s} value={r.by_status[s]} max={max} color={h.color}
                    onClick={() => setDrill({ region: r.region, status: s })} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      <StockDrillDialog open={!!drill} status={drill?.status} region={drill?.region} onClose={() => setDrill(null)} />
    </div>
  );
}
